import { uuidModule } from "@/modules/uuid";
import {
  SpaceAccountCalendarEventModelData,
  SpaceAccountCalendarEventUpsertedSyncUpdateValue,
} from "@/store/calendar/space-account-calendar-event/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IUpdateSpaceAccountCalendarEventOperation } from "@/store/sync/operations/types";
import { SyncModelData, SyncOperationKind, OptimisticSyncUpdate } from "@/store/sync/types";

export class UpdateSpaceAccountCalendarEventOperation extends BaseSyncOperation<IUpdateSpaceAccountCalendarEventOperation> {
  get operationKind(): SyncOperationKind {
    return "UPDATE_SPACE_ACCOUNT_CALENDAR_EVENT";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const optimisticUpdates: OptimisticSyncUpdate<SyncModelData>[] = [];
    const calendarEventUpdate = this.generateUpdateCalendarEventUpdate();

    if (calendarEventUpdate) {
      optimisticUpdates.push(calendarEventUpdate);
    }

    return optimisticUpdates;
  }

  async triggerRecompute() {
    await this.store.spaceAccountCalendarEvents.recompute(this.payload.id);
  }

  protected generateUpdateCalendarEventUpdate(): OptimisticSyncUpdate<SpaceAccountCalendarEventModelData> | null {
    const observable = this.store.spaceAccountCalendarEvents.get(this.payload.id);
    if (!observable) return null;

    const value: SpaceAccountCalendarEventUpsertedSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "SPACE_ACCOUNT_CALENDAR_EVENT",
      model_version: observable.modelVersion ?? 0,
      model_data: {
        ...observable.modelData,
        is_hidden: this.payload.is_hidden,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };

    const syncUpdate: OptimisticSyncUpdate<SpaceAccountCalendarEventModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value,
    };

    return syncUpdate;
  }
}
